/**
 * Mini-modals partagés :
 *   - RefuseRequestModal  : refuser une demande (motif ≥ 10 car.)
 *   - DoneManualModal     : marquer "Fait hors Hana" (note ≥ 10 car.)
 *   - CancelAppointmentModal : annuler un RDV (motif optionnel)
 */
import { useState } from "react";
import api from "@/api";
import { toast } from "sonner";
import { AlertTriangle, X, ArrowRightCircle, CheckCircle2 } from "lucide-react";

function Shell({ title, icon, accent, onClose, children, testId }) {
  return (
    <div
      data-testid={testId}
      className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-end sm:items-center sm:justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className={`flex items-center justify-between p-5 border-b ${accent}`}>
          <div className="flex items-center gap-2">
            {icon}
            <h3 className="font-semibold text-slate-800 text-lg">{title}</h3>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 rounded-lg hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function RefuseRequestModal({ request, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const r = reason.trim();
    if (r.length < 10) {
      toast.error("Motif obligatoire (≥ 10 caractères).");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/appointments/requests/${request.id}/refuse`, { reason: r });
      toast.success("Demande refusée");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell
      testId="refuse-request-modal"
      title="Refuser la demande"
      icon={<AlertTriangle className="w-5 h-5 text-red-600" />}
      accent="bg-red-50"
      onClose={onClose}
    >
      <div className="p-5 space-y-3">
        <p className="text-sm text-slate-600">
          Cette demande sera refusée et le patient sera notifié. Motif obligatoire.
        </p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={4}
          placeholder="Ex : Le patient n'est pas éligible à ce type de RDV pour le moment."
          data-testid="refuse-reason-input"
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
        />
        <p className="text-xs text-slate-400">{reason.length} caractères (min. 10)</p>
      </div>
      <div className="p-5 border-t bg-slate-50 flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 text-slate-600 hover:bg-slate-200 rounded-xl font-medium">
          Annuler
        </button>
        <button
          onClick={submit}
          disabled={submitting || reason.trim().length < 10}
          data-testid="refuse-submit-btn"
          className="flex-1 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50"
        >
          {submitting ? "Envoi..." : "Refuser"}
        </button>
      </div>
    </Shell>
  );
}

export function DoneManualModal({ request, onClose, onDone }) {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    const n = note.trim();
    if (n.length < 10) {
      toast.error("Note obligatoire (≥ 10 caractères).");
      return;
    }
    setSubmitting(true);
    try {
      await api.patch(`/appointments/requests/${request.id}/done-manual`, { note: n });
      toast.success("Demande fermée (fait hors Hana)");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell
      testId="done-manual-modal"
      title="Marquer fait hors Hana"
      icon={<ArrowRightCircle className="w-5 h-5 text-amber-600" />}
      accent="bg-amber-50"
      onClose={onClose}
    >
      <div className="p-5 space-y-3">
        <p className="text-sm text-slate-600">
          La demande sera fermée sans créer de RDV dans Hana. Note métier obligatoire
          (ex : RDV planifié dans le logiciel officiel de la clinique).
        </p>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          rows={4}
          placeholder="Ex : RDV planifié dans Doctolib le 14/05 à 10h30."
          data-testid="done-manual-note-input"
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
        />
        <p className="text-xs text-slate-400">{note.length} caractères (min. 10)</p>
      </div>
      <div className="p-5 border-t bg-slate-50 flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 text-slate-600 hover:bg-slate-200 rounded-xl font-medium">
          Annuler
        </button>
        <button
          onClick={submit}
          disabled={submitting || note.trim().length < 10}
          data-testid="done-manual-submit-btn"
          className="flex-1 py-3 bg-amber-600 text-white rounded-xl font-semibold hover:bg-amber-700 disabled:opacity-50"
        >
          {submitting ? "Envoi..." : "Fermer la demande"}
        </button>
      </div>
    </Shell>
  );
}

export function CancelAppointmentModal({ appointment, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.patch(`/appointments/${appointment.id}/cancel`, {
        cancelReason: reason.trim() || null,
      });
      toast.success("RDV annulé");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Shell
      testId="cancel-appointment-modal"
      title="Annuler ce RDV"
      icon={<AlertTriangle className="w-5 h-5 text-red-600" />}
      accent="bg-red-50"
      onClose={onClose}
    >
      <div className="p-5 space-y-3">
        <p className="text-sm text-slate-600">
          Le patient sera notifié de l'annulation. Motif optionnel (conseillé).
        </p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          rows={3}
          placeholder="Ex : Médecin indisponible — sera reprogrammé."
          data-testid="cancel-reason-input"
          className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
        />
      </div>
      <div className="p-5 border-t bg-slate-50 flex gap-3">
        <button onClick={onClose} className="flex-1 py-3 text-slate-600 hover:bg-slate-200 rounded-xl font-medium">
          Retour
        </button>
        <button
          onClick={submit}
          disabled={submitting}
          data-testid="cancel-appointment-submit"
          className="flex-1 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 disabled:opacity-50"
        >
          {submitting ? "Annulation..." : "Confirmer l'annulation"}
        </button>
      </div>
    </Shell>
  );
}

export function InProgressButton({ request, onDone, small = false }) {
  const [loading, setLoading] = useState(false);
  const trigger = async () => {
    setLoading(true);
    try {
      await api.patch(`/appointments/requests/${request.id}/in-progress`);
      toast.success("Demande prise en charge");
      onDone?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };
  return (
    <button
      onClick={trigger}
      disabled={loading || request.status === "IN_PROGRESS"}
      data-testid={`in-progress-${request.id}`}
      title={request.status === "IN_PROGRESS" ? "Déjà en cours" : "Marquer en cours"}
      className={`${small ? "px-3 py-2" : "px-4 py-2"} flex items-center gap-1.5 rounded-xl text-sm font-medium min-h-[44px] border
        ${request.status === "IN_PROGRESS" ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                                          : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
    >
      <CheckCircle2 className="w-4 h-4" />
      {request.status === "IN_PROGRESS" ? "En cours" : "Prendre en charge"}
    </button>
  );
}
